import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowUp, ArrowDown, Trash2, Eye, EyeOff, Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

// The Plus promo calendar (/admin, Pop-ups). Each entry is a dated card: when it
// is live and fits the viewer, PromoPopup shows it once per snooze period. Order
// here is priority when two are live at once. Mirrors casting's PlusPopupSlot
// calendar, but edited here instead of in code.

type Audience = 'everyone' | 'signed_out' | 'non_members' | 'members';
type Entry = {
  id: string; enabled: boolean; title: string; kicker: string; body: string; imageUrl: string;
  ctaLabel: string; ctaUrl: string; startsAt: string; endsAt: string; audience: Audience; pages: 'home' | 'all'; surfaces: ('plus' | 'casting')[]; snoozeDays: number; productId: string | null;
};
type SearchHit = { _id: string; title: string; published?: boolean; eventDate?: string | null; price?: number; memberPriceCents?: number | null; description?: string };

const AUDIENCE_LABEL: Record<Audience, string> = { everyone: 'Everyone', signed_out: 'Signed out only', non_members: 'Not a paid member (signed out, free, trial lapsed)', members: 'Paid members only' };
const pad = (n: number) => String(n).padStart(2, '0');
// datetime-local works in the admin's own clock; the server stores UTC.
const toLocal = (iso: string) => { const d = new Date(iso); if (!isFinite(d.getTime())) return ''; return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const fromLocal = (s: string) => { const d = new Date(s); return isFinite(d.getTime()) ? d.toISOString() : ''; };
const fmt = (iso: string) => { const d = new Date(iso); return isFinite(d.getTime()) ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '?'; };
const status = (e: Entry) => {
  if (!e.enabled) return { label: 'hidden', variant: 'secondary' as const };
  const now = Date.now(), a = new Date(e.startsAt).getTime(), b = new Date(e.endsAt).getTime();
  if (now < a) return { label: `scheduled · ${fmt(e.startsAt)}`, variant: 'outline' as const };
  if (now >= b) return { label: 'ended', variant: 'secondary' as const };
  return { label: `live · ends ${fmt(e.endsAt)}`, variant: 'default' as const };
};

const blank = (): Entry => {
  const start = new Date(); const end = new Date(Date.now() + 7 * 86400000);
  return { id: `p${Date.now().toString(36)}`, enabled: true, title: '', kicker: '', body: '', imageUrl: '', ctaLabel: 'Save my seat', ctaUrl: '', startsAt: start.toISOString(), endsAt: end.toISOString(), audience: 'everyone', pages: 'all', surfaces: ['plus'], snoozeDays: 1, productId: null };
};

// Pre-fill from a product so an admin edits instead of typing from scratch.
function draftFromProduct(p: SearchHit): Entry {
  const e = blank();
  e.title = String(p.title || '').trim();
  e.productId = p._id;
  e.ctaUrl = `/course/${p._id}`;
  const first = String(p.description || '').split(/\n|\. /)[0].trim();
  const price = (p.price || 0) === 0 ? 'Free.' : (p.memberPriceCents === 0 ? `$${((p.price || 0) / 100).toFixed(0)}, free for Plus members.` : (typeof p.memberPriceCents === 'number' ? `$${((p.price || 0) / 100).toFixed(0)}, $${(p.memberPriceCents / 100).toFixed(0)} for Plus members.` : `$${((p.price || 0) / 100).toFixed(0)}.`));
  e.body = `${first}${first && !/[.!?]$/.test(first) ? '.' : ''} ${price}`.trim();
  if (p.eventDate) {
    const d = new Date(p.eventDate);
    e.kicker = `Live event · ${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`;
    e.endsAt = d.toISOString();
    e.ctaLabel = 'Save my seat';
  } else { e.kicker = 'Featured'; e.ctaLabel = 'Take a look'; }
  return e;
}

export function PromoPopupsPanel() {
  const { toast } = useToast();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [meta, setMeta] = useState<{ updatedAt: string | null; updatedBy: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [searching, setSearching] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/admin/promos', { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to load');
      const d = await r.json();
      setEntries((d.entries || []).map((e: Entry) => ({ ...e, surfaces: Array.isArray(e.surfaces) && e.surfaces.length ? e.surfaces : ['plus'], startsAt: new Date(e.startsAt).toISOString(), endsAt: new Date(e.endsAt).toISOString() })));
      setMeta({ updatedAt: d.updatedAt, updatedBy: d.updatedBy }); setDirty(false);
    } catch (e: any) { toast({ title: 'Could not load the pop-ups', description: e.message, variant: 'destructive' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const update = (id: string, patch: Partial<Entry>) => { setEntries(s => s.map(x => (x.id === id ? { ...x, ...patch } : x))); setDirty(true); };
  const move = (i: number, dir: -1 | 1) => { setEntries(s => { const n = [...s]; const j = i + dir; if (j < 0 || j >= n.length) return s; [n[i], n[j]] = [n[j], n[i]]; return n; }); setDirty(true); };
  const remove = (id: string) => { setEntries(s => s.filter(x => x.id !== id)); setDirty(true); if (editing === id) setEditing(null); };
  const add = (e: Entry) => {
    if (entries.length >= 12) { toast({ title: 'Twelve pop-ups is the cap', variant: 'destructive' }); return; }
    setEntries(s => [...s, e]); setDirty(true); setEditing(e.id); setHits([]); setQ('');
  };
  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try { const r = await fetch(`/api/admin/products/search?q=${encodeURIComponent(q.trim())}`, { credentials: 'include' }); const d = r.ok ? await r.json() : { products: [] }; setHits(d.products || []); }
    catch { setHits([]); } finally { setSearching(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch('/api/admin/promos', { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ entries }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Save failed');
      setDirty(false); setMeta({ updatedAt: d.updatedAt, updatedBy: d.updatedBy });
      toast({ title: 'Pop-ups saved', description: 'Live within a minute (60s cache per server).' });
    } catch (e: any) { toast({ title: 'Not saved', description: e.message, variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const ed = entries.find(e => e.id === editing) || null;
  const field = 'w-full rounded-md border bg-background px-3 py-2 text-sm';

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Pop-ups</CardTitle>
          <CardDescription>
            The promo calendar for Plus and casting. A card shows on the dates you set, to the audience you pick, once per snooze period, then stops on its own. When two are live, the higher one wins. Every card carries the 3-day trial underneath for non-members.
            {meta?.updatedAt ? ` Last saved ${new Date(meta.updatedAt).toLocaleString()}${meta.updatedBy ? ` by ${meta.updatedBy}` : ''}.` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
            <div className="space-y-2">
              {!entries.length && <p className="text-sm text-muted-foreground">Nothing on the calendar. Add a card below.</p>}
              {entries.map((e, i) => {
                const st = status(e);
                return (
                  <div key={e.id} className="flex items-center gap-3 rounded-lg border p-3" style={{ opacity: e.enabled ? 1 : 0.55, background: editing === e.id ? 'rgba(255,214,10,0.08)' : undefined }}>
                    <div className="flex flex-col gap-1">
                      <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="disabled:opacity-30"><ArrowUp size={14} /></button>
                      <button type="button" onClick={() => move(i, 1)} disabled={i === entries.length - 1} className="disabled:opacity-30"><ArrowDown size={14} /></button>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm truncate">{e.title || '(untitled)'}</span>
                        <Badge variant={st.variant}>{st.label}</Badge>
                        <Badge variant="outline">{AUDIENCE_LABEL[e.audience].split(' (')[0]}</Badge>
                        {e.pages === 'home' && <Badge variant="outline">home only</Badge>}
                        {(e.surfaces || ['plus']).includes('casting') && <Badge variant="outline">{(e.surfaces || []).includes('plus') ? 'Plus + casting' : 'casting only'}</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{fmt(e.startsAt)} to {fmt(e.endsAt)} · {e.ctaLabel} to {e.ctaUrl}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant={editing === e.id ? 'default' : 'outline'} onClick={() => setEditing(editing === e.id ? null : e.id)}>{editing === e.id ? 'Close' : 'Edit'}</Button>
                      <Button size="sm" variant="ghost" onClick={() => update(e.id, { enabled: !e.enabled })} title={e.enabled ? 'Hide' : 'Show'}>{e.enabled ? <Eye size={15} /> : <EyeOff size={15} />}</Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(e.id)} title="Remove"><Trash2 size={15} /></Button>
                    </div>
                  </div>
                );
              })}
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <Button size="sm" variant="outline" onClick={() => add(blank())}><Plus size={14} className="mr-1" /> Blank card</Button>
                <span className="text-xs text-muted-foreground">or pre-fill from a product:</span>
                <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') search(); }} placeholder="Search products by title" className="max-w-xs h-8" />
                <Button size="sm" variant="outline" onClick={search} disabled={searching}>{searching ? <Loader2 size={14} className="animate-spin" /> : 'Search'}</Button>
              </div>
              {hits.length > 0 && (
                <div className="rounded-lg border divide-y">
                  {hits.slice(0, 8).map(h => (
                    <button key={h._id} type="button" onClick={() => add(draftFromProduct(h))} className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2">
                      <span className="truncate flex-1">{h.title}</span>
                      {h.eventDate && <Badge variant="secondary">{new Date(h.eventDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</Badge>}
                      {!h.published && <Badge variant="destructive">unpublished</Badge>}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 pt-3">
                <Button onClick={save} disabled={!dirty || saving}>{saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}Save calendar</Button>
                {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {ed && (
        <Card>
          <CardHeader><CardTitle className="text-base">Edit: {ed.title || '(untitled)'}</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <label className="text-sm md:col-span-2">Title<Input value={ed.title} onChange={e => update(ed.id, { title: e.target.value })} maxLength={90} /></label>
            <label className="text-sm">Kicker (small pill above the title)<Input value={ed.kicker} onChange={e => update(ed.id, { kicker: e.target.value })} maxLength={60} placeholder="Live event · Sat Oct 17" /></label>
            <label className="text-sm">Image URL (optional, 16:9)<Input value={ed.imageUrl} onChange={e => update(ed.id, { imageUrl: e.target.value })} placeholder="https://..." /></label>
            <label className="text-sm md:col-span-2">Body<textarea className={field} rows={3} value={ed.body} onChange={e => update(ed.id, { body: e.target.value })} maxLength={400} /></label>
            <label className="text-sm">Button label<Input value={ed.ctaLabel} onChange={e => update(ed.id, { ctaLabel: e.target.value })} maxLength={40} /></label>
            <label className="text-sm">Button link<Input value={ed.ctaUrl} onChange={e => update(ed.id, { ctaUrl: e.target.value })} placeholder="/w/roundtable or https://..." /></label>
            <label className="text-sm">Starts<input type="datetime-local" className={field} value={toLocal(ed.startsAt)} onChange={e => update(ed.id, { startsAt: fromLocal(e.target.value) || ed.startsAt })} /></label>
            <label className="text-sm">Ends (stops on its own)<input type="datetime-local" className={field} value={toLocal(ed.endsAt)} onChange={e => update(ed.id, { endsAt: fromLocal(e.target.value) || ed.endsAt })} /></label>
            <label className="text-sm">Who sees it
              <select className={field} value={ed.audience} onChange={e => update(ed.id, { audience: e.target.value as Audience })}>
                {(Object.keys(AUDIENCE_LABEL) as Audience[]).map(a => <option key={a} value={a}>{AUDIENCE_LABEL[a]}</option>)}
              </select>
            </label>
            <label className="text-sm">Where
              <select className={field} value={ed.pages} onChange={e => update(ed.id, { pages: e.target.value as 'home' | 'all' })}>
                <option value="all">Every page (except checkout, builders, admin)</option>
                <option value="home">Homepage only</option>
              </select>
            </label>
            <div className="text-sm">Show on
              <div className="flex gap-4 mt-2">
                {(['plus', 'casting'] as const).map(sf => (
                  <label key={sf} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={(ed.surfaces || ['plus']).includes(sf)} onChange={e => { const cur = new Set(ed.surfaces || ['plus']); if (e.target.checked) cur.add(sf); else cur.delete(sf); update(ed.id, { surfaces: [...cur] as ('plus' | 'casting')[] }); }} />
                    {sf === 'plus' ? 'Hey Joe Plus' : 'Casting talent pages'}
                  </label>
                ))}
              </div>
            </div>
            <label className="text-sm">Snooze after dismiss (days, 0 = every visit)<Input type="number" min={0} max={30} value={ed.snoozeDays} onChange={e => update(ed.id, { snoozeDays: Math.max(0, Math.min(30, parseInt(e.target.value, 10) || 0)) })} /></label>
            <div className="md:col-span-2">
              <div className="text-xs text-muted-foreground mb-2">Preview</div>
              <div style={{ maxWidth: 420, background: '#FFFBF0', color: '#0A0A0A', borderRadius: 22, border: '2px solid #0A0A0A', overflow: 'hidden', fontFamily: "'Geist', system-ui, sans-serif" }}>
                {ed.imageUrl && <img src={ed.imageUrl} alt="" style={{ display: 'block', width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderBottom: '2px solid #0A0A0A' }} />}
                <div style={{ padding: '20px 20px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {ed.kicker && <div style={{ display: 'inline-flex', alignSelf: 'flex-start', padding: '6px 10px', background: '#0A0A0A', color: '#FFD60A', borderRadius: 999, fontFamily: "'Geist Mono', ui-monospace, monospace", fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{ed.kicker}</div>}
                  <div style={{ fontFamily: "'Red Hat Display', sans-serif", fontWeight: 900, fontSize: 26, lineHeight: 1.02, letterSpacing: '-0.03em' }}>{ed.title || 'Title'}</div>
                  {ed.body && <div style={{ fontSize: 14, lineHeight: 1.45, color: '#3A3732', whiteSpace: 'pre-line' }}>{ed.body}</div>}
                  <div style={{ marginTop: 4, borderRadius: 999, background: '#0A0A0A', color: '#FFFBF0', fontSize: 15, fontWeight: 700, padding: '14px 0', textAlign: 'center' }}>{ed.ctaLabel || 'Save my seat'}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
